import React from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router";
import { format, parseJSON } from "date-fns";
import { Link, Typography, useTheme } from "@mui/material";
import { Booking, InvestmentTypeEnum } from "@rentablo/api/apiv1";
import { GainLossTypography, SecurityImage } from "@rentablo/components";
import CancelledIcon from "@icons/myicons/cancelled.svg?react";
import { BookingType } from "@components";
import { MobileListRow } from "@components/MobileList";
import {
  getValueByInvestmentAndBookingType,
  toCurrency,
  toGermanNumberFormat,
} from "@helpers";
import { useSharedPortfolioContext } from "@providers/SharedPortfolioProvider";
import {
  TransactionStatusIcon,
  transactionNeedsAttention,
} from "@features/investments/investment/components/TransactionStatusIcon";

const nameLinkSx = {
  display: "block",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
} as const;

const CENTS = { minimumFractionDigits: 2 };

interface Props {
  booking: Booking;
  logo?: string | null;
  // Opens the investment overlay, as a tap on a row of the investment list.
  onRowClick?: (booking: Booking) => void;
}

export const BookingMobileRow = ({ booking, logo, onRowClick }: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { isSharedPortfolio } = useSharedPortfolioContext();
  const { investment } = booking;
  const name = investment.standardisedName || investment.name;

  // "50 × € 42,38"; bookings without a price (splits) show the lots alone,
  // ones without lots (fees, taxes) the price alone. Money keeps its cents.
  const price = booking.securityPrice
    ? getValueByInvestmentAndBookingType({
        value: Math.abs(booking.securityPrice),
        currency: booking.currency,
        investmentType: investment.type as InvestmentTypeEnum,
        bookingType: booking.type,
        percentageRange: "0-100",
        quoteNotation: investment.quoteNotation,
        currencyOptions: CENTS,
      })
    : "";
  const lots = booking.numberOfLots
    ? toGermanNumberFormat(booking.numberOfLots, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 4,
      })
    : "";
  const quantity =
    lots && price
      ? `${lots} × ${price}`
      : price ||
        (lots &&
          `${lots} ${t("components.depotsStatistic.bookingsTable.lots")}`);

  const status = {
    dataConsistencyStatus: booking.dataConsistencyStatus,
    dataSource: booking.dataSource,
    isExternalID: Boolean(booking.externalId),
  };

  return (
    <MobileListRow
      testId="booking-mobile-row"
      onClick={onRowClick && (() => onRowClick(booking))}
      leading={<SecurityImage src={logo} width={32} height={32} alt={name} />}
      title={
        <Typography variant="body4" component="div" noWrap minWidth={0}>
          {isSharedPortfolio ? (
            name
          ) : (
            <Link
              component={RouterLink}
              to={`/investments/${investment.id}`}
              underline="hover"
              sx={nameLinkSx}
              // The name opens the investment page, not the overlay.
              onClick={(event: React.MouseEvent) => event.stopPropagation()}
            >
              {name}
            </Link>
          )}
        </Typography>
      }
      titleEnd={
        <GainLossTypography
          value={booking.amount}
          variant="body4"
          component="span"
          flex="none"
          whiteSpace="nowrap"
        >
          {booking.amount
            ? toCurrency(booking.amount, booking.currency, false, CENTS)
            : "–"}
        </GainLossTypography>
      }
      subtitle={
        <>
          <BookingType type={booking.type} size="small" />
          {booking.cancellationId != undefined && (
            <CancelledIcon
              role="img"
              aria-label={t("investment.protocolTable.cancelled")}
              width={14}
              height={14}
              style={{ flex: "none", color: theme.palette.icon.secondary }}
            />
          )}
          {quantity && (
            <Typography
              variant="caption"
              color="textSecondary"
              noWrap
              minWidth={0}
            >
              {quantity}
            </Typography>
          )}
          {transactionNeedsAttention(status) && (
            <TransactionStatusIcon {...status} size={14} />
          )}
        </>
      }
      subtitleEnd={
        <Typography variant="caption" color="textSecondary" flex="none">
          {format(parseJSON(booking.valueDate), "dd.MM.yyyy")}
        </Typography>
      }
    />
  );
};
